import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Send,
  Download,
  FilterList,
  Search,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useBanking } from '../context/BankingContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Transaction, Account } from '../types';

const AccountDetail: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { user } = useAuth();
  const { accounts, transactions, deposit, transfer, blockAccount, unblockAccount } = useBanking();
  const navigate = useNavigate();
  
  const [account, setAccount] = useState<Account | null>(null);
  const [accountTransactions, setAccountTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferToAccount, setTransferToAccount] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(10);

  useEffect(() => {
    if (accountId && accounts.length > 0) {
      const foundAccount = accounts.find(acc => acc.id === accountId);
      if (foundAccount) {
        setAccount(foundAccount);
        // Get transactions for this account
        const relatedTransactions = transactions.filter(
          tx => tx.fromAccountId === accountId || tx.toAccountId === accountId
        );
        setAccountTransactions(relatedTransactions);
        setFilteredTransactions(relatedTransactions);
      } else {
        setError('Account not found');
      }
    }
  }, [accountId, accounts, transactions]);

  // Filter transactions
  useEffect(() => {
    let filtered = [...accountTransactions];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(tx => 
        tx.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.recipientEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(tx => tx.type === filterType);
    }

    // Date filters
    if (dateFrom) {
      filtered = filtered.filter(tx => new Date(tx.timestamp) >= new Date(dateFrom));
    }
    if (dateTo) {
      filtered = filtered.filter(tx => new Date(tx.timestamp) <= new Date(dateTo));
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [accountTransactions, searchTerm, filterType, dateFrom, dateTo]);

  const handleDeposit = async () => {
    // Enhanced validation
    if (!depositAmount || depositAmount.trim() === '') {
      setError('Please enter a deposit amount');
      return;
    }
    
    const amount = parseFloat(depositAmount);
    if (isNaN(amount)) {
      setError('Please enter a valid number');
      return;
    }
    
    if (amount <= 0) {
      setError('Deposit amount must be greater than zero');
      return;
    }
    
    if (amount > 1000000) {
      setError('Deposit amount cannot exceed $1,000,000');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await deposit(accountId!, amount);
      setSuccess(`$${amount.toFixed(2)} deposited successfully!`);
      setDepositDialogOpen(false);
      setDepositAmount('');
    } catch (err) {
      // Display the actual error message from the banking context
      const errorMessage = err instanceof Error ? err.message : 'Failed to deposit funds';
      setError(errorMessage);
      console.error('Deposit error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferAmount || parseFloat(transferAmount) <= 0) {
      setError('Please enter a valid transfer amount');
      return;
    }

    if (!transferToAccount) {
      setError('Please select a destination account');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await transfer(accountId!, transferToAccount, parseFloat(transferAmount), recipientEmail || undefined);
      setSuccess(`$${parseFloat(transferAmount).toFixed(2)} transferred successfully!`);
      setTransferDialogOpen(false);
      setTransferAmount('');
      setTransferToAccount('');
      setRecipientEmail('');
    } catch (err) {
      // Display the actual error message from the banking context
      const errorMessage = err instanceof Error ? err.message : 'Failed to transfer funds';
      setError(errorMessage);
      console.error('Transfer error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUnblock = async () => {
    if (!account) return;
    
    setLoading(true);
    setError('');
    
    try {
      if (account.status === 'active') {
        await blockAccount(account.id);
        setSuccess('Account blocked successfully');
      } else {
        await unblockAccount(account.id);
        setSuccess('Account unblocked successfully');
      }
    } catch (err) {
      // Display the actual error message
      const errorMessage = err instanceof Error ? err.message : 'Failed to update account status';
      setError(errorMessage);
      console.error('Account status update error:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (filteredTransactions.length === 0) {
      setError('No transactions to export');
      return;
    }

    const headers = ['Date', 'Type', 'Description', 'Amount', 'Balance Effect', 'Recipient'];
    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(tx => {
        const isIncoming = getIsIncomingTransaction(tx);
        const balanceEffect = isIncoming ? '+' : '-';
        return [
          new Date(tx.timestamp).toLocaleDateString(),
          tx.type,
          `"${tx.description}"`,
          tx.amount.toFixed(2),
          balanceEffect + tx.amount.toFixed(2),
          tx.recipientEmail || 'N/A'
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `account-${account?.accountNumber}-statement.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccess('Statement exported successfully!');
  };

  const getAccountIcon = (accountType: string) => {
    switch (accountType) {
      case 'checking':
        return <AttachMoney color="primary" />;
      case 'savings':
        return <TrendingUp color="success" />;
      case 'loan':
        return <TrendingDown color="warning" />;
      default:
        return <AttachMoney color="primary" />;
    }
  };

  const getTransactionIcon = (transaction: Transaction) => {
    const isIncoming = getIsIncomingTransaction(transaction);
    return isIncoming ? (
      <TrendingUp color="success" />
    ) : (
      <TrendingDown color="error" />
    );
  };

  const getTransactionColor = (transaction: Transaction) => {
    const isIncoming = getIsIncomingTransaction(transaction);
    return isIncoming ? 'success.main' : 'error.main';
  };

  const getTransactionAmount = (transaction: Transaction) => {
    const isIncoming = getIsIncomingTransaction(transaction);
    return `${isIncoming ? '+' : '-'}$${transaction.amount.toFixed(2)}`;
  };

  const getIsIncomingTransaction = (transaction: Transaction) => {
    // For deposits: always incoming to the account being deposited to
    if (transaction.type === 'deposit' && transaction.fromAccountId === accountId) {
      return true;
    }
    // For loan disbursements: incoming to the account receiving the loan
    if (transaction.type === 'loan_disbursement' && transaction.toAccountId === accountId) {
      return true;
    }
    // For transfers: incoming if this account is the destination
    if (transaction.type === 'transfer' && transaction.toAccountId === accountId) {
      return true;
    }
    // For withdrawals or outgoing transfers: outgoing from this account
    return false;
  };

  // Pagination
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

  // Data is now filtered by user in BankingContext, just exclude current account
  const otherAccounts = accounts.filter(acc => acc.id !== accountId);

  if (!account) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Account not found or loading...
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/accounts')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Account Details
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Account Information Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Box sx={{ mr: 3 }}>
                {getAccountIcon(account.accountType)}
              </Box>
              <Box>
                <Typography variant="h5" gutterBottom>
                  {account.accountType.charAt(0).toUpperCase() + account.accountType.slice(1)} Account
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Account Number: {account.accountNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Opened: {account.createdAt.toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h3" color={account.status === 'active' ? 'primary' : 'error'} gutterBottom>
                ${account.balance.toFixed(2)}
              </Typography>
                                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
           {account.status === 'active' ? <CheckCircle color="success" sx={{ fontSize: 16 }} /> : <Block color="error" sx={{ fontSize: 16 }} />}
           <Typography
             variant="caption"
             component="span"
             sx={{
               px: 1,
               py: 0.5,
               borderRadius: 1,
               backgroundColor: account.status === 'active' ? 'success.main' : 'error.main',
               color: 'white',
               fontSize: '0.75rem',
               fontWeight: 500,
               textTransform: 'capitalize'
             }}
           >
             {account.status}
           </Typography>
         </Box>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDepositDialogOpen(true)}
              disabled={account.status === 'blocked' || loading}
            >
              Deposit
            </Button>
            <Button
              variant="outlined"
              startIcon={<Send />}
              onClick={() => setTransferDialogOpen(true)}
              disabled={account.status === 'blocked' || loading || otherAccounts.length === 0}
              title={otherAccounts.length === 0 ? "Create additional accounts to enable transfers" : "Transfer funds to another account"}
            >
              Transfer
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={exportToCSV}
              disabled={filteredTransactions.length === 0}
            >
              Export Statement
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="outlined"
                color={account.status === 'active' ? 'error' : 'success'}
                startIcon={account.status === 'active' ? <Block /> : <CheckCircle />}
                onClick={handleBlockUnblock}
                disabled={loading}
              >
                {account.status === 'active' ? 'Block Account' : 'Unblock Account'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Transaction History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 200 }}
              size="small"
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="deposit">Deposit</MenuItem>
                <MenuItem value="transfer">Transfer</MenuItem>
                <MenuItem value="withdrawal">Withdrawal</MenuItem>
                <MenuItem value="loan_disbursement">Loan Disbursement</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            
            <TextField
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            
            {(searchTerm || filterType !== 'all' || dateFrom || dateTo) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          {/* Transactions Table */}
          {currentTransactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No transactions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredTransactions.length === 0 && accountTransactions.length > 0
                  ? 'Try adjusting your filters'
                  : 'Start making transactions to see them here'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Recipient/Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentTransactions.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(transaction.timestamp).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(transaction.timestamp).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTransactionIcon(transaction)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {transaction.type.replace('_', ' ').toUpperCase()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={getTransactionColor(transaction)}
                          >
                            {getTransactionAmount(transaction)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {transaction.recipientEmail || 'Internal Transfer'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Deposit Dialog */}
      <Dialog 
        open={depositDialogOpen} 
        onClose={() => setDepositDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Deposit Funds</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Enter the amount you want to deposit to this account:
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Deposit Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
            }}
            inputProps={{
              min: 0.01,
              max: 1000000,
              step: 0.01,
            }}
            helperText="Enter amount between $0.01 and $1,000,000"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDepositDialogOpen(false);
              setDepositAmount('');
            }} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleDeposit} variant="contained" disabled={loading}>
            Deposit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog 
        open={transferDialogOpen} 
        onClose={() => setTransferDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Transfer Funds</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Transfer money to another account:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Transfer Amount"
              type="number"
              fullWidth
              variant="outlined"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
              inputProps={{
                min: 0,
                step: 0.01,
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Destination Account</InputLabel>
              <Select
                value={transferToAccount}
                label="Destination Account"
                onChange={(e) => setTransferToAccount(e.target.value)}
                disabled={otherAccounts.length === 0}
              >
                {otherAccounts.length === 0 ? (
                  <MenuItem disabled>
                    No other accounts available - create additional accounts first
                  </MenuItem>
                ) : (
                  otherAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.accountType.charAt(0).toUpperCase() + acc.accountType.slice(1)} - ****{acc.accountNumber.slice(-4)}
                    </MenuItem>
                  ))
                )}
              </Select>
              {otherAccounts.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  You need at least two accounts to make transfers between them.
                </Typography>
              )}
            </FormControl>
            
            <TextField
              label="Recipient Email (Optional)"
              type="email"
              fullWidth
              variant="outlined"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              helperText="For external transfers or record keeping"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setTransferDialogOpen(false);
              setTransferAmount('');
              setTransferToAccount('');
              setRecipientEmail('');
            }} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleTransfer} 
            variant="contained" 
            disabled={loading || otherAccounts.length === 0}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountDetail; 